import React, { createContext, useState, useContext, useCallback, useMemo, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { uploadAndProcessProjectFile, createProject, supabase } from '../utils/supabaseClient';

const UploadContext = createContext();

export const useUpload = () => useContext(UploadContext);

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export const UploadProvider = ({ children }) => {
  const [uploads, setUploads] = useState({});
  const pollingRef = useRef(null);
  const uploadsRef = useRef(uploads);
  const cancelledRef = useRef(false);

  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  const setUploadStatus = useCallback((id, status, error = null) => {
    setUploads(prev => {
      if (!prev[id]) return prev;
      
      const current = prev[id].status;
      
      // Don't overwrite final states
      if (current === 'complete' || current === 'failed' || current === 'error') {
        return prev;
      }

      return {
        ...prev,
        [id]: { ...prev[id], status, error, progress: 100 },
      };
    });
  }, []);

  const updateUploadProgress = useCallback((id, progress) => {
    setUploads(prev => {
      if (!prev[id]) return prev;
      const status = prev[id].status;
      if (status !== 'uploading' && status !== 'starting') return prev;
      
      return {
        ...prev,
        [id]: { ...prev[id], progress, status: 'uploading' },
      };
    });
  }, []);

  // Check status for a single upload
  const checkUploadStatus = useCallback(async (upload) => {
    const filename = upload.file.name;
    const storagePath = upload.storagePath || `session-uploads/${upload.projectId}/${filename}`;

    if (!upload.projectId) return null; // Not ready yet

    try {
      // Check job status first
      const { data: job } = await supabase
        .from('jobs')
        .select('status, error_message')
        .eq('storage_path', storagePath)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (job) {
        if (job.status === 'failed') {
          return { status: 'failed', error: job.error_message || 'Processing failed' };
        }
        if (job.status === 'completed') {
          return { status: 'complete', error: null };
        }
        if (job.status === 'processing') {
          return { status: 'processing', error: null };
        }
      }

      // Fallback: check session directly
      const { data: session } = await supabase
        .from('prototype_sessions')
        .select('status, meta')
        .eq('project_id', upload.projectId)
        .eq('filename', filename)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (session) {
        const sessionStatus = session.status?.toUpperCase();
        if (sessionStatus === 'COMPLETED') {
          return { status: 'complete', error: null };
        }
        if (sessionStatus === 'FAILED') {
          return { status: 'failed', error: session.meta?.error || 'Processing failed' };
        }
        if (sessionStatus === 'PROCESSING') {
          return { status: 'processing', error: null };
        }
      }

      return null; // No change
    } catch (err) {
      console.error(`[Polling] Error checking ${filename}:`, err);
      return null;
    }
  }, []);

  const pollStatuses = useCallback(async () => {
    if (cancelledRef.current) return;

    const currentUploads = uploadsRef.current;
    const activeUploads = Object.values(currentUploads).filter(u =>
      u.status === 'pending' || u.status === 'processing' || u.status === 'uploading'
    );

    if (activeUploads.length === 0) {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    const now = Date.now();

    // Process uploads sequentially to avoid hammering the DB
    for (const upload of activeUploads) {
      // Skip if still uploading (no job yet)
      if (upload.status === 'uploading') continue;

      // Timeout stale uploads
      if (upload.startedAt && (now - upload.startedAt) > POLL_TIMEOUT_MS) {
        setUploadStatus(upload.id, 'error', 'Processing is taking longer than expected. Please check back later or contact support.');
        continue;
      }

      const result = await checkUploadStatus(upload);
      if (result) {
        setUploadStatus(upload.id, result.status, result.error);
      }
    }

    if (cancelledRef.current) return;

    // Schedule next poll
    pollingRef.current = setTimeout(pollStatuses, POLL_INTERVAL_MS);
  }, [checkUploadStatus, setUploadStatus]);

  // Start/stop polling based on active uploads
  useEffect(() => {
    cancelledRef.current = false;

    const hasActive = Object.values(uploads).some(u =>
      u.status === 'pending' || u.status === 'processing'
    );

    if (hasActive && !pollingRef.current) {
      // Delay first poll to give backend time to create job
      pollingRef.current = setTimeout(pollStatuses, 2000);
    }

    return () => {
      cancelledRef.current = true;
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [uploads, pollStatuses]);

  /**
   * Start an upload - supports two modes:
   * 
   * 1. Legacy mode (from UploadPage): 
   *    startUpload({ file, uploadMode, newProjectName, selectedProjectId })
   * 
   * 2. Direct mode (from ProjectDetail):
   *    startUpload({ file, projectId, folderId, sessionName })
   */
  const startUpload = useCallback(async ({ 
    file, 
    // Legacy mode params
    uploadMode, 
    newProjectName, 
    selectedProjectId,
    // Direct mode params
    projectId: directProjectId,
    folderId = null,
    sessionName = null
  }) => {
    const id = uuidv4();
    
    // Determine if this is direct mode or legacy mode
    const isDirectMode = !!directProjectId;

    setUploads(prev => ({
      ...prev,
      [id]: {
        id,
        file,
        progress: 0,
        status: 'starting',
        error: null,
        projectId: isDirectMode ? directProjectId : (uploadMode === 'existing' ? selectedProjectId : null),
        folderId,
        sessionName,
        startedAt: Date.now(),
      },
    }));

    try {
      let finalProjectId;
      
      if (isDirectMode) {
        // Direct mode - project already exists
        finalProjectId = directProjectId;
      } else if (uploadMode === 'create') {
        // Legacy mode - create new project
        const newProject = await createProject({ name: newProjectName.trim() });
        if (!newProject?.id) throw new Error('Failed to create project');
        finalProjectId = newProject.id;
        
        setUploads(prev => ({
          ...prev,
          [id]: { ...prev[id], projectId: finalProjectId },
        }));
      } else {
        // Legacy mode - existing project
        finalProjectId = selectedProjectId;
      }

      const result = await uploadAndProcessProjectFile(
        file,
        finalProjectId,
        (progress) => updateUploadProgress(id, Math.round(progress.percent)),
        folderId,
        sessionName,  // user-typed name; worker uses it for prototype_sessions.name
      );

      // Store the resolved storage path so polling doesn't need to reconstruct it
      setUploads(prev => ({
        ...prev,
        [id]: { ...prev[id], storagePath: result.path, projectId: finalProjectId },
      }));

      setUploadStatus(id, 'pending');

    } catch (err) {
      console.error('Upload failed:', err);
      setUploadStatus(id, 'error', err.message || 'Upload failed');
    }
  }, [updateUploadProgress, setUploadStatus]);
  
  const clearUpload = useCallback((id) => {
    setUploads(prev => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  // Get uploads for a specific project (useful for showing status in ProjectDetail)
  const getProjectUploads = useCallback((projectId) => {
    return Object.values(uploads).filter(u => u.projectId === projectId);
  }, [uploads]);

  // Check if there are any active uploads for a project
  const hasActiveUploads = useCallback((projectId) => {
    return Object.values(uploads).some(
      u => u.projectId === projectId && 
           ['starting', 'uploading', 'pending', 'processing'].includes(u.status)
    );
  }, [uploads]);

  const value = useMemo(() => ({
    uploads,
    startUpload,
    clearUpload,
    getProjectUploads,
    hasActiveUploads,
  }), [uploads, startUpload, clearUpload, getProjectUploads, hasActiveUploads]);

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
};